import { useQuery, useMutation, useQueryClient } from 'react-query'
import { usuarioService } from '../services/usuario'
import { UsuarioFilters, UsuarioFormData } from '../tipos/usuario'
import { useToast } from '../../../contexts/NotificationContext'

export function useUsuarios(filters?: UsuarioFilters) {
  const toast = useToast()

  return useQuery(['usuarios', filters], () => usuarioService.listarUsuarios(filters), {
    keepPreviousData: true,
    onError: (error: any) => {
      const mensagem = error?.message || 'Erro ao carregar usuários'
      toast.error(mensagem)
    },
  })
}

export function useUsuario(id?: string) {
  const toast = useToast()

  return useQuery(['usuario', id], () => usuarioService.getUsuarioById(id!), {
    enabled: !!id,
    onError: (error: any) => {
      const mensagem = error?.message || 'Erro ao carregar usuário'
      toast.error(mensagem)
    },
  })
}

export function useCreateUsuario() {
  const toast = useToast()
  const queryClient = useQueryClient()

  return useMutation(usuarioService.criarUsuario, {
    onSuccess: () => {
      toast.success('Usuário criado com sucesso!')
      queryClient.invalidateQueries(['usuarios'])
    },
      onError: (error: any) => {
        const detalhes = error?.response?.data
        
        // Se detalhes é uma string, usar diretamente
        if (typeof detalhes === 'string') {
          toast.error(detalhes)
          return
        }
        
        const mensagens =
          detalhes?.errors?.messages ??
          detalhes?.errors ??
          (detalhes && typeof detalhes === 'object' ? Object.values(detalhes).flat() : [])

        const mensagem = Array.isArray(mensagens) && mensagens.length > 0
          ? mensagens.join('\n')
          : error?.message || 'Erro ao criar usuário'

        toast.error(mensagem)
      },
  })
}

export function useUpdateUsuario() {
  const toast = useToast()
  const queryClient = useQueryClient()

  return useMutation(
    ({ id, data }: { id: string; data: UsuarioFormData }) => usuarioService.atualizarUsuario(id, data),
    {
      onSuccess: (_, variables) => {
        toast.success('Usuário atualizado com sucesso!')
        queryClient.invalidateQueries(['usuarios'])
        queryClient.invalidateQueries(['usuario', variables.id])
      },
      onError: (error: any) => {
        const detalhes = error?.response?.data
        // Se detalhes é uma string, usar diretamente
        if (typeof detalhes === 'string') {
          toast.error(detalhes)
          return
        }
        
        const mensagens =
          detalhes?.errors?.messages ??
          detalhes?.errors ??
          (detalhes && typeof detalhes === 'object' ? Object.values(detalhes).flat() : [])

        const mensagem = Array.isArray(mensagens) && mensagens.length > 0
          ? mensagens.join('\n')
          : error?.message || 'Erro ao atualizar usuário'

        toast.error(mensagem)
      },
    }
  )
}

export function useDeleteUsuario() {
  const toast = useToast()
  const queryClient = useQueryClient()

  return useMutation(usuarioService.excluirUsuario, {
    onSuccess: () => {
      toast.success('Usuário excluído com sucesso!')
      queryClient.invalidateQueries(['usuarios'])
    },
    onError: (error: any) => {
      const detalhes = error?.response?.data
      
      // Se detalhes é uma string, usar diretamente
      if (typeof detalhes === 'string') {
        toast.error(detalhes)
        return
      }
      
      const mensagens =
        detalhes?.errors?.messages ??
        detalhes?.errors ??
        (detalhes && typeof detalhes === 'object' ? Object.values(detalhes).flat() : [])

      const mensagem = Array.isArray(mensagens) && mensagens.length > 0
        ? mensagens.join('\n')
        : error?.message || 'Erro ao excluir usuário'

      toast.error(mensagem)
    },
  })
}

export function usePessoasAtivas() {
  const toast = useToast()

  return useQuery(['usuarios-pessoas-ativas'], () => usuarioService.listarPessoasAtivas(), {
    staleTime: 0, // Sempre considerar dados como obsoletos para recarregar quando necessário
    refetchOnMount: true, // Recarregar quando o componente for montado
    onError: (error: any) => {
      const mensagem = error?.message || 'Erro ao carregar pessoas ativas'
      toast.error(mensagem)
    },
  })
}

export function usePapeisDisponiveis() {
  const toast = useToast()

  return useQuery(['papeis-disponiveis'], () => usuarioService.listarPapeis(), {
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
    onError: (error: any) => {
      const mensagem = error?.message || 'Erro ao carregar papéis'
      toast.error(mensagem)
    },
  })
}

